/**
 * Controlling process inter-component processes of the client
 * Knows about other lower level modules
 * @module
 */

var _ = require('optimizely-client-fn');

// Error handling
var createError = require('core/utils/custom_error').create;
var ActivationCodeError = exports.ActivationCodeError = createError('ActivationCodeError');
var ProjectJSError = exports.ProjectJSError = createError('ProjectJSError');

// Core modules
var Action = require('core/action');
var actionTypes = require('core/constants/action_types');
var Analytics = require('core/analytics');
var ApiEvents = require('api/api_events');
var Audience = require('core/audience');
var CoreApiEvents = require('core/api_events');
var Data = require('./data');
var DateEnv = require('core/env/date');
var Decision = require('core/decision');
var di = require('core/di');
var Directive = require('core/directive');
var DocumentEnv = require('core/env/document');
var enums = require('core/constants/enums');
var EventEmitter = require('core/event_emitter');
var EventEmitterEnums = require('core/constants/event_emitter');
var Exec = require('core/exec');
var Flux = require('core/flux');
var Group = require('core/group');
var Layer = require('core/layer');
var LocalStorage = require('core/env/local_storage').LocalStorage;
var Logger = require('core/utils/logger');
var PendingEvents = require('core/pending_events');
var performanceEnv = require('core/env/performance');
var PluginManager = require('core/plugin_manager');
var poll = require('core/utils/poll');
var PromiseEnv = require('core/lib/promise').Promise;
var PromiseUtil = require('core/utils/promise');
var Relay = require('core/analytics/relay');
var Timeout = require('core/utils/timeout');
var timingHelpers = require('core/action/timing_helpers');
var View = require('core/view');
var Visitor = require('core/visitor');
var WindowEnv = require('core/env/window');

// Store modules - pull things out of DI
var di = require('core/di');
var SessionStore = di.get('stores/session');
var AudienceDataStore = di.get('stores/audience_data');
var ActionDataStore = di.get('stores/action_data');
var CleanupStore = di.get('stores/cleanup');
var DirectiveStore = di.get('stores/directive');
var GlobalStore = di.get('stores/global');
var GroupDataStore = di.get('stores/group_data');
var LayerDataStore = di.get('stores/layer_data');
var LayerStore = di.get('stores/layer');
var PendingEventsStore = di.get('stores/pending_events');
var PluginsStore = di.get('stores/plugins');
var RumStore = di.get('stores/rum');
var VisitorStore = di.get('stores/visitor');
var ViewDataStore = di.get('stores/view_data');
var ViewStore = di.get('stores/view');
var VisitorIdStore = di.get('stores/visitor_id');
var VisitorBucketingStore = di.get('stores/visitor_bucketing');
var XDomainStore = di.get('stores/xdomain');

var Api = require('./api');
var ErrorMonitor = require('./error_monitor');
var RUM = require('./rum');

// interval used for polling the document is ready for initial view tracking
// use a lower interval for testing as to not make the tests take forever
var TRACK_VIEW_POLLING_INTERVAL = __TEST__ ? 10 : 1000;

var shouldBatch = __EXPERIMENTAL_BATCH_VIEW_ACTIVATIONS__;
var EXPERIMENTAL_EDGE_SPA = __EXPERIMENTAL_EDGE_SPA__;
var EXPERIMENTAL_TRACKING_ONLY = __EXPERIMENTAL_TRACKING_ONLY__;
var isEdgeFollowonSnippet = EXPERIMENTAL_EDGE_SPA || EXPERIMENTAL_TRACKING_ONLY;
var EXPERIMENTAL_WEB_SDK = __EXPERIMENTAL_WEB_SDK__;

/**
 * @constant
 * Time, in milliseconds, to wait for the xdomain iframe to load if waitForOriginSync api used
 * @type {number}
 * @private
 */
var XDOMAIN_IFRAME_TIMEOUT = 1000;

// Note(tyler): assign "private" properties that must be mocked for tests to Controller (instead of exports)
// and add @private doc
var Controller = exports;


/**
 * Initialize the client by loading data and setting up observation
 * @param {Object} clientData the DATA object containing audiences, experiences, etc
 * @param {Plugin[]} plugins
 * @return {Promise} resolves when all initializers are done, or some fail (ignored by main)
 */
 exports.initialize = function(config): Promise<any> {
  this.initGlobalStore(config.clientData);
  return this.initializePlugins(config.plugins);
};

/**
 * Initialize the global store
* @param {Object} clientData the DATA object containing audiences, experiences, etc
*/
exports.initGlobalStore = function(clientData:any) {

  var clientData = clientData;
  Data.normalizeClientData(clientData);

  if (__INVARIANT__) {
    require('core/utils/invariant').isType('ClientData', clientData);
  }

  // Log errors from core
  EventEmitter.on({
    filter: {
      type: 'error',
    },
    handler: onError,
  });

  // TODO: invariants for clientData and config.dataProviders
  Flux.dispatch(actionTypes.DATA_LOADED, {
    data: clientData,
  });
  Logger.log('Initialized with DATA:', clientData);

};

/**
 * Initialize the client by setting up observation
 * loading data should already be done
 * @param {Plugin[]} plugins
 * @return {Promise} resolves when all initializers are done, or some fail (ignored by main)
 */
exports.initializePlugins = function(plugins:Plugin[]): Promise<any> {
  if (__PROFILE__ || __RUM_VERBOSE__) {
    performanceEnv.timeEnd('preInitialize');
    performanceEnv.time('ClientInitialize');
  }

  invokeHighPriorityApiCalls();

  // Now that we've processed the Cookie Domain API, we can maybe
  // (re)set or remove the optimizelyOptOut cookie depending on what
  // the visitor has specified via the OptOut API, query parameter,
  // or pre-existing cookie [ listed in order of precedence ].
  //
  // If the cookie already exists, we may end up duplicating it if it
  // was set to a different domain than we were given through the
  // Cookie Domain API or what domain we use by default. For this
  // optimizelyOptOut cookie though, we would rather be able to
  // extend it - accepting this risk that we may duplicate it.
  // When removing the cookie, we will remove all optimizelyOptOut
  // cookies across all domains we can access on the current page.
  // (See Cookie.remove)
  Directive.setOptOut(DirectiveStore.shouldOptOut());

  // Bail if the client has been disabled by pre-pushed API call.
  if (DirectiveStore.isDisabled() || DirectiveStore.shouldOptOut()) {
    Logger.log('Controller / Is disabled');
    return;
  }

  // Queue RUM beacons
  RUM.queueBeacons();

  if (DocumentEnv.isReady()) {
    Flux.dispatch(actionTypes.SET_DOMCONTENTLOADED);
  } else {
    DocumentEnv.addReadyHandler(function() {
      Flux.dispatch(actionTypes.SET_DOMCONTENTLOADED);
    });
  }

  if (!isEdgeFollowonSnippet) {
    performanceEnv.time('projectJS');
    var projectJSFunction = GlobalStore.getProjectJS();
    if (_.isFunction(projectJSFunction)) {
      try {
        // run project js code
        Exec.apply(projectJSFunction);
      } catch (e) {
        Logger.error('Error while executing projectJS: ', e);
        CoreApiEvents.emitError(new ProjectJSError(e));
      }
    }
    performanceEnv.timeEnd('projectJS');
  }

  // Register "built-in" plugins directly; log errors internally
  (__PROFILE__ || __RUM_VERBOSE__) && performanceEnv.time('pluginsRegister');
  _.each(plugins || [], function(registerFn) {
    try {
      registerFn(PluginManager);
    } catch(e) {
      if (__PREVIEW__) {
        Logger.error('Error loading plugin:', e);
      } else {
        CoreApiEvents.emitInternalError(e);
      }
    }
  });

  // Register Plugin-based entities (e.g., custom analytics and audience integrations) with Exec
  _.each(GlobalStore.getPlugins() || [], function(registerFn) {
    try {
      Exec.apply(registerFn, [PluginManager]);
    } catch(e) {
      if (__PREVIEW__) {
        Logger.error('Error loading extension:', e);
      } else {
        CoreApiEvents.emitError(e);
      }
    }
  });
  (__PROFILE__ || __RUM_VERBOSE__) && performanceEnv.timeEnd('pluginsRegister');

  Relay.load();
  // Track later, after we've had a chance to process additional "disable" and "disable tracking" API calls,
  // load requisite plugins for integrations settings deserialization,
  // and after we've had a chance to load the requisite data into the VisitorStore.

  // setup store observation
  // TODO: figure out if this will cause issues when calling flux.reset()
  // as it currently resets all observers
  var unobserverToken = EventEmitter.on({
    filter: {
      type: 'lifecycle',
      name: 'activated'
    },
    handler: function() {
      VisitorStore.observe(Visitor.persistVisitorProfile);
      LayerStore.observe(Visitor.persistLayerStates);
      SessionStore.observe(Visitor.persistSessionState);
      PendingEventsStore.observe(PendingEvents.persistPendingEvents);
      // Disable persisting bucketing decisions if this is an Edge project, since
      // sticky bucketing is not supported in Edge projects.
      if (!EXPERIMENTAL_EDGE_SPA) {
        VisitorBucketingStore.observe(Visitor.persistVisitorBucketingStore);
      }
      //Added so the observers are not set up multiple times on additional 'activated' event
      EventEmitter.off(unobserverToken);
    }
  });

  EventEmitter.on({
    filter: {
      type: 'lifecycle',
      name: 'viewsActivated',
    },
    handler: exports.onViewsActivated,
  });

  /**
   * When a page is deactivated, we must cancel all in progress changes
   * which are applying or waiting to apply based on that page being active.
   */
  EventEmitter.on({
    filter: {
      type: 'lifecycle',
      name: 'pageDeactivated',
    },
    handler: exports.onPageDeactivated,
  });

  exports.initializeApi();

  // Load saved pending log events from local storage
  var pendingEvents = PendingEvents.getPendingEvents();
  if (pendingEvents) {
    Flux.dispatch(actionTypes.LOAD_PENDING_EVENTS, {
      events: pendingEvents,
    });
    // Send any pending log events
    PendingEvents.retryPendingEvents(pendingEvents);
  }

  // Let's innie know that api is initialized and ready to talk to
  if (__EDITOR__) {
    EventEmitter.emit({
      type: EventEmitterEnums.TYPES.EDITOR,
      name: 'ready',
      data: {},
    });
  }

  EventEmitter.on({
    filter: {
      type: 'lifecycle',
      name: 'activate',
    },
    handler: exports.activate,
  });

  ApiEvents.emitInitializedEvent();

  if (!DirectiveStore.shouldActivate()) {
    return PromiseEnv.resolve();
  }

  var initializers = [];
  if (__XDOMAIN__ && !XDomainStore.isDisabled()) {
    (__PROFILE__ || __RUM_VERBOSE__) && performanceEnv.time('xdomainSetup');
    var xDomainStoragePromise = exports.initializeXDomainStorage();
    initializers.push(xDomainStoragePromise);

    var waitForOriginSyncCalled = Boolean(XDomainStore.getCanonicalOrigins());
    if (waitForOriginSyncCalled) {
      // Wait [up to XDOMAIN_IFRAME_TIMEOUT] for xDomain iframe to load, then activate
      var timeout = Timeout.makeTimeoutPromise(XDOMAIN_IFRAME_TIMEOUT);

      PromiseEnv.race([xDomainStoragePromise, timeout])['catch'](function(error) {
        Logger.error('Failed to initialize xDomain storage: ', error);
        // Swallow errors and timeouts
        // No matter which one resolves (or rejects) first, continue
      }).then(emitActivate)['catch'](ErrorMonitor.handleError);
    } else {
      // Not waiting for xDomain iframe to load
      emitActivate();
      (__PROFILE__ || __RUM_VERBOSE__) && performanceEnv.timeEnd('ClientInitialize');
    }
  } else {
    emitActivate();
    (__PROFILE__ || __RUM_VERBOSE__) && performanceEnv.timeEnd('ClientInitialize');
  }

  return PromiseEnv.all(initializers);
};


/**
 * Emit activate event
 *
 * If `__EXPERIMENTAL_ACTIVATION_DELAY__` is set, and there are 0 Layers, delay activation by that number of ms
 *
 * @private
 */
function emitActivate() {
  if (__XDOMAIN__) {
    (__PROFILE__ || __RUM_VERBOSE__) && performanceEnv.timeEnd('xdomainSetup');
  }
  if (__EXPERIMENTAL_REQUIRE_API_ACTIVATE__) {
    Logger.log('Not activating (require API)');
    Flux.dispatch(actionTypes.SET_RUM_DATA, {
      data: {
        activateDfd: true,
      },
    });
    return;
  }
  var delay = __EXPERIMENTAL_ACTIVATION_DELAY__;
  if (_.isNumber(delay) && LayerDataStore.getCount() === 0) {
    Logger.log('Activating after delay of', delay, 'ms because no Experiments are running');
    Flux.dispatch(actionTypes.SET_RUM_DATA, {
      data: {
        activateDfd: true,
      },
    });
    WindowEnv.setTimeout(ApiEvents.emitActivateEvent, delay);
  } else {
    ApiEvents.emitActivateEvent();
  }
}

function onError(ev) {
  if (__PREVIEW__) {
    Logger.error('Uncaught error:', ev.data.error);
  } else {
    ErrorMonitor.handleError(ev.data.error, ev.data.metadata);
  }
}

/**
 * Main activation function of the Client
 *
 * * Cleanup any prior listeners
 * * Notify stores of activation
 * * Set visitorId
 * * Start building visitor profile
 * * Decide global holdback
 * * Add event listeners
 * * Activate static views
 *
 * @return {Promise} resolves when all deferred processes are resolved, except for event listeners
 */
exports.activate = function(): Promise<any> {
  try {
    // track any deferred actions here
    var deferreds = [];

    Logger.log('Activated client');

    // This is needed in the case where activate is called multiple times per runtime (single page apps)
    executeCleanupFns(enums.Lifecycle.preActivate);

    let reactivationFlag = false;

    if(EXPERIMENTAL_WEB_SDK && GlobalStore.getActivationId()) {
      Logger.log('Reactivating client.');
      reactivationFlag = true;
    }

    // Generate and set a new activationId
    var now = DateEnv.now();
    Flux.dispatch(actionTypes.ACTIVATE, {
      activationId: String(now),
      activationTimestamp: now,
    });

    var views = ViewDataStore.getAll();
    // (Re-)set each view to inactive
    View.registerViews(views, reactivationFlag);

    // Set the visitor ID and load all associated data from localStorage.
    // This overrides any data that may have been added to the stores that are backed by localStorage,
    // so any prior functionality that has data to add a store should delay that via
    //   Flux.dispatch(actionTypes.ADD_CLEANUP_FN, {
    //     lifecycle: enums.Lifecycle.postVisitorProfileLoad,
    //     cleanupFn: ...
    //   })
    // rather than dispatching to the store immediately.
    Visitor.setId(Visitor.getOrGenerateId());

    // Finish tracking any redirect decision that may have taken us to this page load,
    // now that we have the requisite data in the VisitorIdStore and VisitorStore.
    deferreds.push(Analytics.trackPostRedirectDecisionEvent());

    // Store variationIdMap and preferredLayerMap, if they exist
    // Merge them so that we don't overwrite anything set by bucketVisitor API
    Flux.dispatch(actionTypes.MERGE_VARIATION_ID_MAP, {
      variationIdMap: Visitor.getVariationIdMap(),
    });

    // Store variationIdMap and preferredLayerMap, if they exist
    // Merge them so that we don't overwrite anything set by bucketVisitor API
    Flux.dispatch(actionTypes.MERGE_CONTEXTUAL_MAB_MAP, {
      contextualMabMap: Visitor.getContextualMabMap(),
    });

    // Store odp segment for the visitor if exists
    Flux.dispatch(actionTypes.SET_ODP_SEGMENTS, Visitor.getOdpSegments());

    if (__GROUPS__) {
      Flux.dispatch(actionTypes.MERGE_PREFERRED_LAYER_MAP, {
        preferredLayerMap: Visitor.getPreferredLayerMap(),
      });
    }

    // Apply any post-load functions
    executeCleanupFns(enums.Lifecycle.postVisitorProfileLoad);

    // Returns a promise, but we don't use the resolution
    deferreds.push(startVisitorPopulation(PluginsStore.getAllPlugins(enums.PluginTypes.visitorProfileProviders))
      .then(function() {
        Logger.log('Populated visitor profile');
      })
    );

    // Decide global holdback state
    var globalDecisionTicket = getDecisionTickets();
    var globalDecision = Decision.decideGlobal(globalDecisionTicket);
    Logger.log('Made global decision', globalDecisionTicket, '->', globalDecision);
    Flux.dispatch(actionTypes.RECORD_GLOBAL_DECISION, globalDecision);

    // Track the ClientActivation event before we activate any Views or decide any Layers
    var activationEvent = Analytics.trackClientActivation();
    if (activationEvent) {
      Logger.log('Tracked activation event', activationEvent);
    } else {
      Logger.log('Not tracking activation event');
    }

    // Immediately activate some views and set up delayed activation for the remaining views.
    var viewsToActivate = Controller.setUpViewActivation(views);
    var viewsActivated = [];
    if (shouldBatch) {
      // Activate all views (and decide dependent campaigns) in one group
      viewsActivated = View.activateMultiple(viewsToActivate);
    } else {
      // Activate views (and decide dependent campaigns) one-by-one (for backwards compatibility)
      // TODO: remove this branch when possible
      _.each(viewsToActivate, function(view) {
        viewsActivated = viewsActivated.concat(View.activateMultiple([view]));
      });
    }

    if ((EXPERIMENTAL_EDGE_SPA || EXPERIMENTAL_WEB_SDK) && RumStore.getSampleRum()) {
      // track rum data here
      Flux.dispatch(actionTypes.RECORD_VIEWS_INITIALLY_ACTIVATED_COUNT, {
        viewsInitiallyActivatedCount: viewsActivated.length,
      });
    }

    executeCleanupFns(enums.Lifecycle.postViewsActivated);

    executeCleanupFns(enums.Lifecycle.postActivate);

    ApiEvents.emitActivatedEvent();
    return PromiseEnv.all(deferreds).then(function() {
      EventEmitter.emit({
        type: EventEmitterEnums.TYPES.LIFECYCLE,
        name: 'activateDeferredDone',
      });
      Logger.log('All immediate effects of activation resolved');
    }, CoreApiEvents.emitError);
  } catch(error) {
    CoreApiEvents.emitError(error);
    return PromiseEnv.reject(error);
  }
};

/**
 * Activate immediate activation views, and set up activation for callback/polling types
 * @param {View[]} views
 */
Controller.setUpViewActivation = function(views:View[]) {
  var immediatelyActivatedViews = [];
  _.each(views, function(view) {
    if (_.isBoolean(ViewStore.getViewState(view.id).isActive) && View.isActivationTypeImmediate(view.activationType)) {
      Logger.debug('Skipping page: already evaluated, presumably at the edge', View.description(view));
    } else if (View.shouldTriggerImmediately(view.activationType)) {
      immediatelyActivatedViews.push(view);
    } else if (view.activationType === enums.ViewActivationTypes.callback) {
      // TODO: make it pluggable
      Logger.debug('Setting up conditional activation for Page', View.description(view));
      Controller.activateViewOnCallback(view);
    } else if (view.activationType === enums.ViewActivationTypes.polling) {
      // TODO: make it pluggable
      Logger.debug('Setting up polling activation for Page', View.description(view));
      poll.pollFor(_.partial(Exec.apply, view.activationCode), null, _.partial(timingHelpers.isTimedOut, DateEnv.now()))
          .then(function() {
            View.activateMultiple([view]);
          })
          ['catch'](function(error) {
        Logger.warn('Failed to activate view ', view, error);
      });
    } else if (view.activationType !== enums.ViewActivationTypes.manual) {
      CoreApiEvents.emitError(new Error('Unknown view activationType: ' + view.activationType));
    }
  });
  return immediatelyActivatedViews;
};

/**
 * @callback ViewActivationFn
 * @param {Function} activateFn Arguments to this function are interpreted as in v1.page
 * @param {Object} options
 * @param {string} options.pageId
 * @param {boolean} options.isActive
 */

/**
 * Set up view to be activated on callback
 * @param {Object} view
 * @param {ViewActivationFn} view.activationCode
 */
Controller.activateViewOnCallback = function(view:any) {
  var activateFn = function(config) {
    var args = _.extend({}, config, {
      pageName: view.apiName,
      type: 'page',
    });
    Api.push(args);
  };
  // create the options are for the activationCode function
  var options = {
    pageId: view.id,
  };
  Object.defineProperty(options, 'isActive', {
    get: function() {
      return ViewStore.isViewActive(view.id);
    },
  });

  try {
    Exec.apply(view.activationCode, [activateFn, options]);
  } catch(error) {
    var activationCodeError = new ActivationCodeError(
      '(' + error.toString() + ') in activationCode for ' + View.description(view)
    );
    CoreApiEvents.emitError(activationCodeError, {originalError: error, userError: true});
  }
};

/**
 * Decide the group of layers for the given group of views
 * @param {Object} config
 * @param {Object} config.data
 * @param {View[]} config.data.views
 * @return {Promise} Resolves when all layers related to group of views are decided, and views are tracked
 */
exports.onViewsActivated = function(config:any): Promise<any> {
  var views = config.data.views;
  var viewIds = _.map(views, 'id');
  var allTracked;

  try {
    if (!VisitorIdStore.getBucketingId()) {
      throw new Error('View activated with no visitorId set');
    }
    var trackLayerDecisions = decideRelevantLayersASAP(viewIds)['catch'](CoreApiEvents.emitError);

    allTracked = PromiseEnv.all(_.map(views, function(view) {
      var track = function () {
        (__PROFILE__ || __RUM_VERBOSE__) && performanceEnv.time('sendViewActivate');
        View.parseViewTags(view);
        var activationEvent = Analytics.trackViewActivation(view);
        if (activationEvent) {
          Logger.log('Tracked activation for Page', View.description(view), activationEvent);
        } else {
          Logger.log('Not Tracking activation for Page', View.description(view));
        }
      };

      // Track view event only once DOM is ready to enable tags to be included
      if (DocumentEnv.isReady()) {
        return PromiseEnv.resolve(track());
      } else {
        return poll.pollFor(DocumentEnv.isReady, TRACK_VIEW_POLLING_INTERVAL).then(track);
      }
    }));
    // TODO(tyler): add test for error handling
    return PromiseEnv.all([trackLayerDecisions, allTracked]);
  } catch(e) {
    CoreApiEvents.emitError(e);
  }
};

/**
 * Handle a page deactivated event by fetching all change appliers for the action's
 * changeSet and invoking undo or cancel on them (depending on the value of page.undoOnDeactivation).
 *
 * @param {PageDeactivatedApiEvent} event
 * @private
 */
exports.onPageDeactivated = function(event:PageDeactivatedApiEvent) {
  var page = event.data.page;
  var actionIdsForPage = ActionDataStore.getAllActionIdsByPageId(page.id);
  _.each(actionIdsForPage, function(actionId) {
    var actionState = ActionDataStore.getActionState(actionId);
    if (!actionState) {
      // This action is not active
      return;
    }
    _.each(actionState, function(changeApplier, changeId) {
      if (changeApplier.cancel) {
        try {
          changeApplier.cancel();
          Logger.debug('Controller / Canceled change', changeId, 'observation due to deactivation of page:', page);
        } catch(err) {
          Logger.error('Controller / Error canceling change', changeId, 'observation upon deactivation of page.', err);
        }
      }
      if (page.undoOnDeactivation) {
        if (changeApplier.undo) {
          try {
            changeApplier.undo();
            Logger.debug('Controller / Undid change', changeId, 'due to deactivation of page:', page);
          } catch(err) {
            Logger.error('Controller / Error undoing change upon deactivation of page.', err);
          }
        }
      }
    });
    Flux.dispatch(actionTypes.REMOVE_ACTION_STATE, {
      actionId: actionId,
    });
    Logger.debug('Controller / Undid changes and/or canceled change observation due to deactivation of page:', page, actionId);
  });
};

/**
 * Invoke high priority calls that were pushed before we register the global API
 * @private
 */
function invokeHighPriorityApiCalls() {
  if (_.isArray(__GLOBAL__)) {
    // Apply and filter our high-priority calls (so they aren't called again in initialize)
    __GLOBAL__ = _.filter(__GLOBAL__, function(apiCall) {
      var highPriorityOnly = true;
      return !Api.push(apiCall, highPriorityOnly);
    });
  }
}

/**
 * Initialize the API by invoking any calls made to `optimizely.push` before the client loaded
 * and then expose the global API
 * @private
 */
exports.initializeApi = function() {
  var publicApi:any = {
    push: Api.push,
  };

  if (!EXPERIMENTAL_TRACKING_ONLY) {
    publicApi.get = Api.get;
  }

  // check if any `.push(..)` calls were made before initialization and invoke
  var initialApiCalls = __GLOBAL__;

  if (_.isArray(initialApiCalls)) {
    _.each(initialApiCalls, function(apiCall) {
      publicApi.push(apiCall);
    });
  }

  // global data -- doesn't change
  publicApi.data = {note: 'Obsolete, use optimizely.get(\'data\') instead'};

  // visitor state -- observe stores
  publicApi.state = {} as any;

  __GLOBAL__ = publicApi;
};

/**
 * @param {Object} items
 *        Key/value pairs to write to localStorage.
 * @returns {Object}
 *        The given `items`.
 * @private
 */
exports.persistItemsWithId = function (items:any): any {
  _.each(items, function (value, key) {
    if (Visitor.checkKeyForVisitorId(key)) {
      LocalStorage.setItem(key, value);
    }
  });
  return items;
};

/**
 *
 * @returns {Promise} resolves when XDomain is loaded and keys are fetched and persisted.
 * @private
 */
exports.initializeXDomainStorage = initializeXDomainStorage;
function initializeXDomainStorage() {
  // Detect if the visitor is an existing visitor who is new on this origin
  // TODO(CJS-1470): remove this when resolved
  var XDomainStorage = require('core/utils/xdomain_storage');
  var existingVisitor = !!Visitor.getCurrentId();
  var hasData = existingVisitor ? Visitor.hasSomeData() : false;

  if (existingVisitor) {
    if (hasData) {
      Logger.log('xd / Existing visitor; has data on this origin');
    } else {
      Logger.log('xd / Existing visitor; new to this origin');
    }
  } else {
    Logger.log('xd / New visitor');
  }

  // Compute URL of xdomain sync document, which is an account-level resource.
  var accountId = GlobalStore.getAccountId();
  // var accountStorageOrigin = __XDOMAIN_ORIGIN__.replace('__SUBDOMAIN__', 'a' + accountId + '.');
  var accountStorageOrigin = 'https://snippet-cjs-9442.pages.dev';
  var accountStoragePath = '/a.html';

  // Preemptively subscribe to updates
  XDomainStorage.subscribe(function(key, value) {
    debugger
    if (Visitor.checkKeyForVisitorId(key)) {
      LocalStorage.setItem(key, value);
    }
  });

  var promise = XDomainStorage.fetchAll()
    .then(function(data) {
      debugger
      if (!VisitorIdStore.getVisitorIdLocator()) {
        var canonicalOrigins = XDomainStore.getCanonicalOrigins();
        if (canonicalOrigins) {
          var userId = XDomainStorage.getXDomainUserId(data, canonicalOrigins);
          if (userId) {
            Logger.log('Syncing cross-origin visitor randomId:', userId);
            Visitor.maybePersistVisitorId({
              randomId: userId,
            });
          }
        }
      }
      Visitor.deleteOldForeignData();
      return data;
    })
    .then(exports.persistItemsWithId)
    .then(function(data) {
      Visitor.loadForeignData();

      if (existingVisitor && !hasData) {
        var loadedData = !_.isEmpty(data);
        Logger.debug('xd / Loaded foreign data? ', loadedData);
        recordXDAttempt(loadedData);
      }

      Logger.log('Loaded visitor data from foreign origins');
      ApiEvents.emitOriginsSyncedEvent();
    }, function(e) {
      if (existingVisitor && !hasData) {
        Logger.debug('xd / Failed to load foreign data:', e);
        recordXDAttempt(false, e);
      }

      throw e;
    });

  return PromiseEnv.all([
    XDomainStorage.load(accountStorageOrigin, accountStoragePath)
      ['catch'](function(e) {
        Logger.debug('xd / Failed to load iframe:', e);
        if (existingVisitor && !hasData) {
          recordXDAttempt(false, e);
        }

        throw e;
      }),
    promise['catch'](function(err) {
      // Assume errors from loadForeignData are because visitorId isn't set in store yet due to waitForOriginSync
      Logger.debug('xd / Ignored error syncing foreign data (expected if waitForOriginSync used):', err.message);
      Logger.debug('xd / Enqueuing sync to happen after visitorId set.');
      Flux.dispatch(actionTypes.ADD_CLEANUP_FN, {
        lifecycle: enums.Lifecycle.postVisitorProfileLoad,
        cleanupFn: ApiEvents.emitOriginsSyncedEvent,
      });
    })
  ]);
}

function recordXDAttempt(result, error?) {
  Flux.dispatch(actionTypes.SET_RUM_DATA, {
    data: {
      extras: {
        xdAttempt: result,
        xdError: error ? error.toString() : undefined,
      },
    },
  });
}

/**
 * @param {VisitorProfileProvider[]} providers
 * @return {Promise}
 * @private
 */
function startVisitorPopulation(providers:VisitorProfileProvider[]): Promise<any> {
  // by this time any visitor profile in localStorage will be initialized
  // in the VisitorStore and we can cut a fresh VisitorProfile
  var existingVisitorProfile = VisitorStore.getVisitorProfile();
  // Start the visitor data fetching process, pass the existingVisitorProfile
  // to make sure certain sticky data providers (like referrer) dont get overridden
  return Visitor.populateEagerVisitorData(providers, existingVisitorProfile);
}

/**
 * Get decisionTicket(s) including audience membership for the provided audiences, for each relevant view activated
 *
 * @param {Audience[]} [audiences] - audiences to include (defaults to empty list)
 * @param {Layer} [layer]
 *   Layer to decide. Not provided when decisionTicket is created for purpose of determining global holdback
 * @param {Array.<string>} [viewIds]
 *   Views to get tickets for (if the layer policy specifies viewId relevant). Not provided when decisionTicket is created
 *   for purpose of determining global holdback
 * @return {DecisionTicket[]}
 * @private
 */
function getDecisionTickets(audiences?:Audience[], layer?:Layer, viewIds?:string[]): DecisionTicket[] {
  (__PROFILE__ || __RUM_VERBOSE__) && performanceEnv.time('getDecisionTicket');
  audiences = audiences || [];

  var globalHoldback = GlobalStore.getGlobalHoldbackThreshold();

  var visitorBucketingId = VisitorIdStore.getBucketingId();
  if (!visitorBucketingId) {
    throw new Error('bucketingId not set');
  }
  // get most recent VisitorProfile
  var visitorProfile = VisitorStore.getVisitorProfile();

  var preferredVariationMap;
  // Perf optimization: No need to consult store if this is an Edge related snippet,
  // since sticky bucketing is not supported in Edge projects.
  if (layer && !EXPERIMENTAL_EDGE_SPA) {
    var variationIdMap = VisitorBucketingStore.getVariationIdMap();
    preferredVariationMap = variationIdMap[layer.id];
  }

  var decisionTicketData = {
    bucketingId: visitorBucketingId,
    visitorProfile: visitorProfile,
    audiences: audiences,
    globalHoldback: globalHoldback,
    preferredVariationMap: preferredVariationMap,
    layer: layer,
  };

  // print the DecisionTicket used to make the CDE decision
  if (layer && viewIds && Decision.isPageIdRelevant(layer)) {
    return _.map(viewIds, function(viewId) {
      return Decision.createTicket(_.extend({}, decisionTicketData, {pageId: viewId}));
    });
  } else {
    return [Decision.createTicket(decisionTicketData)];
  }
}

/**
 * Returns a GroupDecisionTicket (a base type that gets extended into a full DecsionTicket)
 * which contains all information required to choose which layer from a group to bucket the
 * visitor into.
 *
 * @param {Group} group
 * @returns {GroupDecisionTicket}
 * @private
 */
function getGroupDecisionTicket(group:Group): GroupDecisionTicket {
  return {
    bucketingId: VisitorIdStore.getBucketingId(),
    preferredLayerId: VisitorBucketingStore.getPreferredLayerMap()[group.id],
  };
}

/**
 * @param {string[]} viewIds
 * @returns {Promise}
 *        Fulfills with an array of layers that were attempted to be decided/executed.
 *        Never rejects.
 * @private
 */
function decideRelevantLayersASAP(viewIds:string[]): Promise<any> {
  var relevantLayers = LayerDataStore.getAllByPageIds(viewIds);

  var forceVariationIds = DirectiveStore.getForceVariationIds();
  var forceAudienceIds = DirectiveStore.getForceAudienceIds();

  var disableMutex = !_.isEmpty(forceVariationIds);
  if (disableMutex) {
    Logger.log('Force variations are in use. Disabling mutual exclusivity.');
  }

  // sortedLayers is an object with shape like
  //   { groups: {...id: Group}, individual: [...Layer] }
  // where groups is the set of Groups represented among the relevantLayers,
  // and individual is the set of relevantLayers which aren't in a Group.
  //
  // Effectively "turn off" mutex (treat all layers as ungrouped)
  // if DirectiveQueryParams.FORCE_VARIATIONS is in use. Note that this doesn't
  // cause layers that _aren't_ the target of the force variation to inadvertently
  // possibly have their changes applied, since any time force variations are in effect,
  // any layer decisions which aren't affected by the supplied force variation IDs
  // are dummy decisions. See Decision.getDummyLayerDecision.
  var sortedLayers = (__GROUPS__ && !disableMutex)
    ? _.reduce(relevantLayers, function (result, layer) {
        if (layer.groupId) {
          if (!result.groups[layer.groupId]) {
            result.groups[layer.groupId] = GroupDataStore.get(layer.groupId);
          }
        } else {
          result.individual.push(layer);
        }

        return result;
      }, { groups: {}, individual: [] })
    : { individual: relevantLayers };


  Logger.log('Deciding Campaigns/Experiments for Page(s)', viewIds);

  if (__GROUPS__) {
    var groupDescriptions = _.map(sortedLayers.groups, Group.description).join(', ');
    Logger.log('Groups:', groupDescriptions);
  }

  var individualDescriptions = _.map(sortedLayers.individual, Layer.description).join(', ');
  Logger.log('Campaigns/Experiments not in Groups (by Campaign id):', individualDescriptions);

  var groupsDone = __GROUPS__ && _.map(sortedLayers.groups, _.partial(decideAndExecuteGroupASAP, forceVariationIds, forceAudienceIds, viewIds)) || [];
  var individualsDone = _.map(sortedLayers.individual, function(layer) {
    // Filter to those views which are relevant to this layer
    var relevantViewIds = _.filter(layer.pageIds, _.partial(_.includes, viewIds));
    return exports.decideAndExecuteLayerASAP(forceVariationIds, forceAudienceIds, relevantViewIds, layer);
  });
  var allDone = groupsDone.concat(individualsDone);

  // Rejects if a single layer execution fails!
  return PromiseEnv.all(allDone)
    .then(function logResult(layersAttempted) {
      // "attempted" means that its actions were attempted/enqueued, OR that
      // the visitor was not in the layer's cohort and no actions were executed,
      // OR a known error occurred and no layer could be chosen at all.
      // "resolved" corresponds to any of the non-error cases.
      var layersResolved = _.filter(layersAttempted, function(l) { return !!l; });

      Logger.log('All Campaigns/Experiments for Page(s) (by Campaign id)', viewIds, 'resolved:',
                 _.map(layersResolved, Layer.description).join(', '));

      return layersResolved;
    });
}

/**
 * Attempts to choose which layer in a group to apply to the visitor,
 * and then execute that layer's changes
 *
 * @param {string[]} forceVariationIds
 * @param {string[]} forceAudienceIds
 * @param {string[]} viewIds
 * @param {Group} group
 * @return {PromiseEnv.<Layer|undefined, DecisionError>} Fulfills when the layer is resolved;
 *                                      rejects in error cases. Resolves with the
 *                                      chosen layer if possible (undefined if not possible),
 *                                      or the error reason.
 * @private
 */
function decideAndExecuteGroupASAP(forceVariationIds:string[], forceAudienceIds:string[], viewIds:string[], group:Group): Promise<any> {
  try {
    var groupDecisionTicket = getGroupDecisionTicket(group);
    var groupDecision = Decision.decideGroup(group, groupDecisionTicket);

    if (groupDecision.reason) {
      Logger.debug('Not activating Group', Group.description(group), '; reason:', groupDecision.reason);
      if (RumStore.getSampleRum()) {
        // Send info to rum even though the group isn't activating
        Flux.dispatch(actionTypes.RECORD_LAYER_FEATURE_USAGE, {
          feature: 'mutex',
          entityId: group.id,
        });
      }
      return PromiseEnv.resolve();
    }

    var selectedLayer = LayerDataStore.get(groupDecision.layerId);

    // May happen, e.g., for cross-project groups or custom snippets
    if (!selectedLayer) {
      Logger.debug('Visitor was bucketed into a Campaign ' +
                   '(' + groupDecision.layerId + ') which is not in this snippet');
      return PromiseEnv.resolve();
    }

    var relevantActiveViewIds = _.filter(selectedLayer.pageIds, _.partial(_.includes, viewIds));
    if (_.isEmpty(relevantActiveViewIds)) {
      Logger.debug('Not activating Group', Group.description(group), '; reason: visitor was bucketed ' +
                   'into a Campaign/Experiment not related to the currently-activating Page(s)');
      return PromiseEnv.resolve();
    }
    if (RumStore.getSampleRum()) {
      Flux.dispatch(actionTypes.RECORD_LAYER_FEATURE_USAGE, {
        feature: 'mutex',
        entityId: group.id,
      });
    }
    return exports.decideAndExecuteLayerASAP(forceVariationIds, forceAudienceIds, relevantActiveViewIds, selectedLayer);
  } catch(e) {
    Logger.error('Error getting decision for Group', Group.description(group), '; ', e);
    return PromiseEnv.reject(e);
  }
}

/**
 * Attempts to activate the given layer and apply its changes.
 *
 * @param {string[]} forceVariationIds
 * @param {string[]} forceAudienceIds
 * @param {string[]} viewIds
 * @param {Layer} layer
 * @returns {Promise} Fulfills with the given layer when the attempt to execute completed without error
 *                    (though layer may not have been executed, e.g., for cohort reasons). If it did execute,
 *                    it may have (indeed, hopefully) done so synchronously. Rejects with an Error, otherwise.
 * @private
 */
exports.decideAndExecuteLayerASAP = decideAndExecuteLayerASAP;
function decideAndExecuteLayerASAP(forceVariationIds:string[], forceAudienceIds:string[], viewIds:string[], layer:Layer) {
  return new PromiseEnv(function(resolve, reject) {
    (__PROFILE__ || __RUM_VERBOSE__) && performanceEnv.time('decideAndExecuteLayer');
    // Catch errors when whenDecisionTicketReady executes its callback synchronously.
    try {
      whenDecisionTicketReady(layer, forceVariationIds, forceAudienceIds, viewIds, function(decisionTickets) {
        _.each(decisionTickets, function(decisionTicket) {
          var actionViewIds = decisionTicket.pageId ? [decisionTicket.pageId] : viewIds;
          Logger.debug('Deciding layer: ', layer, 'with decisionTicket: ', decisionTicket, 'and actionViewIds: ', actionViewIds);
          decideAndExecuteLayer(layer, forceVariationIds, forceAudienceIds, actionViewIds, decisionTicket);
        });
        resolve(layer);
      });
    } catch(e) {
      Logger.error('Error getting decision for Campaign: ' + Layer.description(layer), e);
      reject(e);
    }
    (__PROFILE__ || __RUM_VERBOSE__) && performanceEnv.timeEnd('decideAndExecuteLayer');
  });
}

/**
 * Attempts to activate the given layer and apply its changes.
 * @param {Layer} layer
 * @param {string[]} forceVariationIds
 * @param {string[]} forceAudienceIds
 * @param {string[]} viewIds
 * @param {DecisionTicket} decisionTicket
 * @private
 */
function decideAndExecuteLayer(layer, forceVariationIds, forceAudienceIds, viewIds, decisionTicket) {
  var layerDescription = Layer.description(layer);
  Logger.log('Activating Campaign', layerDescription, 'on Page(s)', viewIds);

  // Override audiences for the purposes of layer selection
  if (forceAudienceIds.length) {
    Logger.log('Applying force audienceIds:', forceAudienceIds, 'to Campaign', layerDescription);
    decisionTicket = _.cloneDeep(decisionTicket);
    decisionTicket.audienceIds = forceAudienceIds;
  }

  var decision = exports.decideLayer(layer, decisionTicket, forceVariationIds);

  // Prepare our actions, which may update the PendingRedirectStore.
  var shouldIgnoreGlobalHoldback = !!(forceVariationIds.length || forceAudienceIds.length);
  var actionData = exports.getActionsForDecision(layer, decision, shouldIgnoreGlobalHoldback);
  var viewActions = filterActionsToViewIds(actionData.actions, viewIds);

  if (!__EXPERIMENTAL_WEB_SDK__ && !__LIVE_CHANGES__) {
    Logger.warn('Not preparing actions because LIVE_CHANGES is false');
  } else if (actionData.maybeExecute) {
    prepareActionsFromLayerDecision(viewActions, layer, decision, viewIds);
  }

  // Track the decision event
  // Send one decision per relevant view id to comply with the pricing definition of impressions
  // viewIds is a list of all relevant view ids for which the decision is made
  _.forEach(viewIds, function() {
    Analytics.trackDecisionEvent(decision, decisionTicket);
  });

  // emit a public event
  ApiEvents.emitLayerDecided({
    layer: layer,
    decisionTicket: decisionTicket,
    decision: decision
  });

  if (decision.error) {
    throw decision.error;
  }
  if (RumStore.getSampleRum()) {
    Flux.dispatch(actionTypes.RECORD_LAYER_POLICY_USAGE, {
      policy: layer.policy,
      layerId: layer.id,
    });

    // Send to RUM all change types associated with the current layer and active view,
    // even if the visitor is excluded due to traffic allocation/holdbacks/audiences
    var changeTypes = gatherChangeTypesFromActions(actionData.actions);
    Flux.dispatch(actionTypes.RECORD_CHANGE_TYPE_USAGE, {
      changeTypes: _.keys(changeTypes),
      layerId: layer.id,
    });

    if (!_.isEmpty(layer.integrationSettings)) {
      Flux.dispatch(actionTypes.RECORD_INTEGRATION_USAGE, {
        integrations: Layer.getIntegrationTypes(layer),
        layerId: layer.id,
      });
    }
  }

  if (!Decision.isInCohort(decision)) {
    Logger.log('Not activating Campaign: ' + Layer.description(layer) + '; not in the cohort because:', decision.reason);
    return;
  }

  if (!__EXPERIMENTAL_WEB_SDK__ && !__LIVE_CHANGES__) {
    Logger.warn('Not executing actions because LIVE_CHANGES is false');
  } else if (actionData.maybeExecute) {
    // Now execute the prepared actions.
    executeActionsFromLayerDecision(viewActions, layer, decision, viewIds);
  }
}

/**
 * @param {ActionData[]} actions
 * @param {Layer} layer Primarily for logging.
 * @param {string[]} viewIds Primarily for logging.
 * @param {Decision} decision Primarily for logging.
 * @private
 */
function prepareActionsFromLayerDecision(actions:ActionData[], layer:Layer, decision:Decision, viewIds:string[]) {
  var layerDescription = Layer.description(layer);
  Logger.log('Preparing actions', actions,
             'for Campaign', layerDescription,
             'on Page(s)', viewIds);

  _.forEach(actions, Action.prepareAction);
}

/**
 * @param {ActionData[]} viewActions Null if no live changes
 * @param {Layer} layer Primarily for logging.
 * @param {string[]} viewIds Primarily for logging.
 * @param {Decision} decision Primarily for logging.
 * @return {Promise[]} Fulfills when action execution terminates, successfully or not.
 * @private
 */
function executeActionsFromLayerDecision(viewActions:ActionData[], layer:Layer, decision:Decision, viewIds:string[]): Promise<any>[] {
  var layerDescription = Layer.description(layer);
  Logger.log('Executing actions', viewActions,
             'for Campaign', layerDescription,
             'on Page(s)', viewIds);

  return PromiseEnv.all(_.map(viewActions, function(viewAction) {
    return Action.executePreparedAction(viewAction)
      .then(_.partial(ApiEvents.emitActionAppliedEvent, viewAction));
  })).then(function () {
    Logger.log('All page actions for', decision, 'applied:', viewActions);
    ApiEvents.emitActionsForDecisionAppliedEvent(decision, viewActions);
  })['catch'](function (e) {
    Logger.warn('Error evaluating page actions for decision', decision, 'because:', e);
  });
}

/**
 * Collects all the change types used in a set of actions
 * @param {Actions[]} actions
 * @return {Object} Change types used, with placeholder value
 * @private
 */
function gatherChangeTypesFromActions(actions:Action[]): any {
  var changeTypes = {} as any;
  _.each(actions, function(action) {
    _.each(action.changeSet, function(change) {
      if (!changeTypes[change.type]) {
        changeTypes[change.type] = true;
      }
    });
  });
  return changeTypes;
}

/**
 * Callback with DecisionTickets:
 *
 * * immediately if forceVariation/forceAudience Ids specified
 * * otherwise when pending audiences resolve, or
 * * after layer-specified activation timeout (if before pending audiences resolve)
 *
 * @param {Layer} layer - Layer to get ticket for
 * @param {string[]} forceVariationIds
 * @param {string[]} forceAudienceIds
 * @param {string[]} viewIds
 * @param {Function} callback - function to call with decision tickets list, once we have it, for this layer
 * @private
 */
function whenDecisionTicketReady(layer:Layer, forceVariationIds:string[], forceAudienceIds:string[], viewIds:string[], callback:Function) {
  if (forceVariationIds.length || forceAudienceIds.length) {
    // Don't need to wait for any audiences
    callback(getDecisionTickets([], undefined, viewIds));
    return;
  }

  // Determine relevant audiences by inspecting layer
  var audienceIds = Layer.relatedAudienceIds(layer);
  var audiences = _.reduce(audienceIds, function(memo, audienceId) {
    var audience = AudienceDataStore.get(audienceId);
    // Remove audiences that aren't in the store for some reason
    if (!!audience) {
      memo.push(audience);
    }
    return memo;
  }, []);

  var matchersMap = PluginsStore.getAllPlugins(enums.PluginTypes.audienceMatchers);
  if (RumStore.getSampleRum()) {
    // Get audience types and send usage to rum
    var audienceTypesUsed = {} as any;
    _.each(audiences, function(audience) {
      _.extend(audienceTypesUsed, collectAudienceTypes(audience.conditions, matchersMap));
    });

    if (!_.isEmpty(audienceTypesUsed)) {
      var audienceTypes = _.keys(audienceTypesUsed);
      Flux.dispatch(actionTypes.RECORD_AUDIENCE_USAGE, {
        audienceTypes: audienceTypes,
        layerId: layer.id,
      });
    }
  }

  const uniqueAudienceTypes = extractTypesFromAudiences(audiences);

  // CJS-7568 After client activation, call populateLazyVisitorData
  var providers = PluginsStore.getAllPlugins(enums.PluginTypes.visitorProfileProviders);
  var existingVisitorProfile = VisitorStore.getVisitorProfile();
  Visitor.populateLazyVisitorData(providers, existingVisitorProfile, uniqueAudienceTypes);

  // Wait for those audiences to resolve, or give up after layer timeout
  waitForPendingAudiences(audiences, matchersMap, Layer.getActivationTimeout(layer), function computeTicketAndContinue() {
    var decisionTickets = getDecisionTickets(audiences, layer, viewIds);

    _.map(decisionTickets, function(decisionTicket) {
      logAudiences(decisionTicket, audiences, layer);
    });

    callback(decisionTickets);
  });
}

/**
 * * CJS-7577 Extracts the types from the conditions of the provided audiences.
 * * This function iterates over the provided audiences, and for each audience, it extracts the 'type' property from its conditions.
 * * If the 'type' is 'time_and_day', it also adds 'currentTimestamp' to the list of types.
 * @param {Audience[]} audiences - The audiences from which to extract the types.
 * @returns {Set<string>} The Set of types extracted from the audience conditions. If an error occurs during the extraction, an empty Set is returned.
 * @throws Will log an error message and return an empty Set if an error occurs during the extraction process.
 */
function extractTypesFromAudiences(audiences: Audience[]): Set<string> {
  Logger.debug('Audience list for extracting types', audiences);
  try {
      const uniqueAudienceTypes = new Set<string>(
          _(audiences)
              .map('conditions')
              .flattenDeep()
              .filter((item) => typeof item !== 'string')
              .map('type')
              .value()
      );

      // If "time_and_day" is found, add "currentTimestamp"
      if (uniqueAudienceTypes.has('time_and_day')) {
          uniqueAudienceTypes.add('currentTimestamp');
      }

      Logger.debug('Extracted unique types from audiences', uniqueAudienceTypes);
      return uniqueAudienceTypes;
  } catch (error) {
      Logger.error(
          'An error occurred while extracting types from audiences:',
          error
      );
      return new Set();
  }
}

/**
 * Collect the audience conditions needed for the layer
 * @param {Condition[]} conditions
 * @param {Object.<AudienceMatcher>} matchersMap
 * @private
 */
function collectAudienceTypes(conditions:Condition[], matchersMap:Record<string, AudienceMatcher>) {
  var audienceTypes = {} as any;
  _.each(conditions, function(condition) {
    if (_.isArray(condition)) {
      _.extend(audienceTypes, collectAudienceTypes(condition, matchersMap));
    } else if (_.isObject(condition)) {
      // Don't include audience types that don't have a matcher
      if (matchersMap[condition.type]) {
        audienceTypes[condition.type] = true;
      }
    }
  });
  return audienceTypes;
}

/**
 * Log audiences that the decisionTicket indicates the visitor was {in,ex}cluded from
 *
 * @param {DecisionTicket} decisionTicket
 * @param {Audience[]} audiences - List of audiences against which the visitor is evaluated
 * @param {Layer} layer - Layer relating to the decisionTicket
 * @private
 */
function logAudiences(decisionTicket:DecisionTicket, audiences:Audience[], layer:Layer) {
  var inAudiences = _.map(decisionTicket.audienceIds, _.bind(AudienceDataStore.get, AudienceDataStore));
  var outAudiences = _.filter(audiences, function(audience) {
    return !_.includes(decisionTicket.audienceIds, audience.id);
  });

  Logger.log('When deciding Campaign', Layer.description(layer),
             'visitor is in audiences:', describeAudiences(inAudiences),
             'and not in audiences:', describeAudiences(outAudiences));
}

/**
 * Generate a list of audience names/audiences (alternating) from list of audiences
 * @param {Audience[]} audiences
 * @returns {Array.<string>}
 * @private
 */
function describeAudiences(audiences:Audience[]): string[] {
  var desc = [];
  _.each(audiences, function(audience) {
    desc.push(audience.name, audience);
  });
  return desc;
}

/**
 * Wait for a list of audiences to be determined before calling callback (if they are not pending will resolve
 * immediately).
 *
 * @param {Audience[]} audiences - list of audiences for which to determine required fields
 * @param {Object} matchersMap - Map of audiences matchers
 * @param {number} timeout - ms after which to "give up" and reject
 * @param {Function} callback - function to call when ready to get DecisionTicket (all pending audiences resolved, or timeout)
 * @private
 */
function waitForPendingAudiences(audiences:Audience[], matchersMap:any, timeout:number, callback:Function) {
  var requiredFields = _.reduce(audiences, function(res, audience) {
    return _.extend(res, Audience.requiredAudienceFieldsForConditions(audience.conditions, matchersMap));
  }, {});

  // Get all pending attribute values (promises) if no current value defined
  var pendingVisitorAttributes = _.reduce(requiredFields, function(memo, fieldKey) {
    if (_.isUndefined(Visitor.getAttribute(fieldKey))) {
      var value = Visitor.getPendingAttributeValue(fieldKey);
      if (!_.isUndefined(value)) {
        memo.push(value);
      }
    }
    return memo;
  }, []);

  if (pendingVisitorAttributes.length === 0) {
    return callback();
  }

  var remainingAudiences = [].concat(audiences);
  var allAudiencesResolved = PromiseUtil.firstToResolve(_.map(pendingVisitorAttributes, function(fieldValue) {
    // As soon as each field value resolves
    return PromiseEnv.resolve(fieldValue)
      /**
       * Check if all audiences are have defined values
       * @throws {Error} if not all audiences have defined values
       */
      .then(function assertAllAudiencesDefined() {
        var visitorProfile = VisitorStore.getVisitorProfile();

        remainingAudiences = _.filter(remainingAudiences, function(audience) {
           return _.isUndefined(Audience.isInAudience(visitorProfile, audience, matchersMap));
        });

        if (!_.isEmpty(remainingAudiences)) {
          throw new Error('At least one audience is still pending');
        }
      });
  }));

  PromiseEnv.race([
    // All audiences resolved
    allAudiencesResolved,
    // Timeout expired
    new PromiseEnv(function(resolve, reject) {
      WindowEnv.setTimeout(reject, timeout);
    }),
  ]).then(function() {
    Logger.log('Activating Campaign after pending Audiences resolved', audiences);
    callback();
  }, function() {
    Logger.log('Activating Campaign after timeout on Audiences', audiences);
    callback();
  });
}

/**
 * Sets up a layer by waiting for it to be activated then passes it through
 * the process of printing a DecisionTicket, recording the DecisionTicket,
 * sending it to the CDE and recording the Decision
 *
 * @param {Layer} layer
 * @param {DecisionTicket} decisionTicket
 * @param {string[]} forceVariationIds
 * @return {Decision} the Decision that was made
 * @private
 */
exports.decideLayer = decideLayer;
function decideLayer(layer, decisionTicket, forceVariationIds) {
  (__PROFILE__ || __RUM_VERBOSE__) && performanceEnv.time('sendDecision');
  var decision;
  var layerDescription = Layer.description(layer);

  if (forceVariationIds.length) {
    decision = Decision.getDummyLayerDecision(layer, forceVariationIds);
  } else {
    // invoke CDE to get decision
    decision = Decision.decideLayer(
      layer,
      decisionTicket
    );
  }

  // associate the Decision with the LayerState
  Logger.log('Recording decision for Campaign', layerDescription, decisionTicket, '->', decision);
  Layer.recordLayerDecision(layer.id, decisionTicket, decision);

  // Don't persist any bucketing information for Edge experiments (including groups block in case we support mutex later)
  // If we eventually add support for sticky bucketing in Edge, those buckets will have to be persisted in *cookies*
  // which are readable/writable by the edge-decider.
  if (!EXPERIMENTAL_EDGE_SPA) {
    if (decision.variationId && decision.experimentId) {
      Visitor.updateVariationIdMap(layer.id, decision.experimentId, decision.variationId);
    }

    if (__GROUPS__ && layer.groupId) {
      Visitor.updatePreferredLayerMap(layer.groupId, layer.id);
    }
  }
  return decision;
}

/**
 * Executes all cleanup fns for the given lifecycle
 * @private
 */
function executeCleanupFns(lifecycle) {
  var cleanupFns = CleanupStore.getCleanupFns(lifecycle);
  if (cleanupFns.length > 0) {
    while (cleanupFns.length > 0) {
      cleanupFns.shift()();
    }
    // only dispatch CLEAR_CLEANUP_FN to remove cleanup fns from store if there are any cleanupFns
    // this saves the slight overhead of dispatching an action we know to be a no-op
    Flux.dispatch(actionTypes.CLEAR_CLEANUP_FN, {
      lifecycle: lifecycle,
    });
  }
}

/**
 * Get actions for the decided layer, experiment, and variation.
 *
 * @param {Layer} layer
 * @param {Decision} decision
 * @param {boolean} ignoreGlobalHoldback - Whether or not to ignore the state
 *                                         of the global holdback.
 * @returns {Object} actions: List of actions for the decision
 *                   maybeExecute: True if the visitor is considered part of the treatment, else False
 *                              Non-treatment scenarios are global holdback, layer holdback, missing traffic
 *                              allocation for AB test, or failing audience conditions for all experiments in layer.
 * @private
 */
exports.getActionsForDecision = getActionsForDecision;
function getActionsForDecision(layer, decision, ignoreGlobalHoldback) {
  var layerDescription = Layer.description(layer);
  var logPrefix = 'NOT applying changes for Campaign';
  var actionData = {
    actions: [],
    maybeExecute: false,
  };

  actionData.actions = [].concat(
    ActionDataStore.getLayerActions(decision.layerId) || [],
    ActionDataStore.getExperimentActions(decision.experimentId) || [],
    ActionDataStore.getExperimentVariationActions(decision.experimentId, decision.variationId) || []
  );

  if (!ignoreGlobalHoldback && GlobalStore.isGlobalHoldback()) {
    // Visitor is held back from all layers
    Logger.log(logPrefix, layerDescription, '(visitor is in global holdback)');
    return actionData;
  }
  if (decision.isLayerHoldback) {
    // Visitor is held back from this layer
    Logger.log(logPrefix, layerDescription, '(visitor is in layer holdback)');
    return actionData;
  }
  if (!(decision.experimentId && decision.variationId)) {
    // Empty decision returned (not eligible for any experimentId or variationId)
    Logger.log(logPrefix, layerDescription, '(visitor is not eligible for any Experiments)');
    return actionData;
  }

  actionData.maybeExecute = true;
  Logger.log('Got Actions for Campaign:', layerDescription, actionData.actions);

  return actionData;
}

/**
 * Given a list of actions, return the actions that can be applied when the given View is active.
 *
 * @param {ActionData[]} actions
 * @param {string[]} viewIds
 * @returns {ActionData[]}
 * @private
 */
function filterActionsToViewIds(actions:ActionData[], viewIds:string[]): ActionData[] {
  return _.filter(actions, function (action) {
    return _.isUndefined(action.pageId) || _.includes(viewIds, action.pageId);
  });
}

if (__TEST__) {
  // expose for testing
  exports.getDecisionTickets = getDecisionTickets;
  exports.getGroupDecisionTicket = getGroupDecisionTicket;
  exports.decideAndExecuteGroupASAP = decideAndExecuteGroupASAP;
  exports.decideAndExecuteLayer = decideAndExecuteLayer;
  exports.decideRelevantLayersASAP = decideRelevantLayersASAP;
  exports.executeCleanupFns = executeCleanupFns;
  exports.startVisitorPopulation = startVisitorPopulation;
  exports.XDOMAIN_IFRAME_TIMEOUT = XDOMAIN_IFRAME_TIMEOUT;
  exports.getActionsForDecision = getActionsForDecision;
  exports.setShouldBatch = function(batch) {
    shouldBatch = batch;
  };
  exports.getShouldBatch = function() {
    return shouldBatch;
  };
  exports.setTrackingOnly = function(trackingOnly) {
    EXPERIMENTAL_TRACKING_ONLY = trackingOnly;
  };
  exports.setEdgeSpa = function(edgeSpa) {
    EXPERIMENTAL_EDGE_SPA = edgeSpa;
  };
  exports.setIsEdgeFollowonSnippet = function(isFollowon) {
    isEdgeFollowonSnippet = isFollowon;
  };
}
